import type { PieceState } from './types';

/** Детерминированный RNG по числовому seed (LCG). */
function createRng(seed: number): () => number {
  let state = (seed >>> 0) || 1;
  return () => {
    state = (1664525 * state + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

/**
 * Делители N (в порядке возрастания).
 */
function divisors(N: number): number[] {
  const out: number[] = [];
  for (let d = 1; d <= N; d++) if (N % d === 0) out.push(d);
  return out;
}

/**
 * Возвращает rows/cols по difficulty и размерам изображения так, чтобы кусочки были квадратными.
 * rows*cols = difficulty; подбираем пару (rows, cols), при которой cellHeight/cellWidth ≈ 1.
 */
export function getRowsCols(
  difficulty: number,
  imageWidth?: number,
  imageHeight?: number
): { rows: number; cols: number } {
  const N = Math.max(1, Math.floor(difficulty));
  const divs = divisors(N);

  if (imageWidth != null && imageHeight != null && imageWidth > 0 && imageHeight > 0) {
    let best = { rows: divs[0] ?? 1, cols: N / (divs[0] ?? 1) };
    let bestScore = Infinity;
    for (const cols of divs) {
      const rows = N / cols;
      const cellW = imageWidth / cols;
      const cellH = imageHeight / rows;
      const pieceAspect = cellH / cellW;
      const score = Math.abs(pieceAspect - 1);
      if (score < bestScore) {
        bestScore = score;
        best = { rows, cols };
      }
    }
    return best;
  }

  const side = Math.round(Math.sqrt(N));
  const cols = divs.reduce((a, d) => (Math.abs(d - side) < Math.abs(a - side) ? d : a), divs[0]);
  return { rows: N / cols, cols };
}

/**
 * Строит сетку деталей по размеру изображения и сложности.
 * Возвращает размеры ячейки и список PieceState с correctX/correctY.
 * Если передан seed, начальные (x, y) перемешиваются детерминированно.
 */
export function buildGridPieces(
  imageWidth: number,
  imageHeight: number,
  difficulty: number,
  seed?: number
): { cellWidth: number; cellHeight: number; pieces: PieceState[] } {
  const { rows, cols } = getRowsCols(difficulty, imageWidth, imageHeight);
  const cellWidth = imageWidth / cols;
  const cellHeight = imageHeight / rows;

  const pieces: PieceState[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const id = row * cols + col;
      const correctX = col * cellWidth;
      const correctY = row * cellHeight;
      pieces.push({
        id,
        row,
        col,
        correctX,
        correctY,
        x: correctX,
        y: correctY,
        placed: false,
      });
    }
  }

  if (seed !== undefined && seed !== 0) {
    const rng = createRng(seed);
    // Fisher–Yates: shuffle массив позиций (correctX, correctY) и присвоить piece[i].x/y
    const positions = pieces.map((p) => ({ x: p.correctX, y: p.correctY }));
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }
    pieces.forEach((p, i) => {
      p.x = positions[i].x;
      p.y = positions[i].y;
    });
  }

  return { cellWidth, cellHeight, pieces };
}

/**
 * Детерминированный seed по puzzleId и difficulty (для повторяемого расклада).
 */
export function getSeedForPuzzle(puzzleId: string, difficulty: number): number {
  let h = 0;
  const s = `${puzzleId}:${difficulty}`;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}
